import { createInstance, Config } from "@hackler/react-sdk";
import { HACKLE_DEVTOOLS_STORAGE_KEY, HACKLE_VARIATIONS, registerExperiment } from "goi_common";

const config: Config = {
  auto_track_page_view: true,
};
export const HackleClient = createInstance(
  process.env.NEXT_PUBLIC_HACKLE_BROWSER_SDK_KEY as string,
  config,
);

type VariationMap = Record<number, string>;

/** useCustomVariation의 useIsDevEnvironment와 동일 판정을 훅 없이 수행. */
const isDevEnvironment = (): boolean => {
  const isDevelopment = process.env.NODE_ENV === "development";
  const isPreview = process.env.NEXT_PUBLIC_VERCEL_ENV === "preview";
  const isProductionExplicitlySet = process.env.NEXT_PUBLIC_IS_PRODUCTION === "true";
  return (isDevelopment || isPreview) && !isProductionExplicitlySet;
};

/**
 * 이벤트 핸들러 등 React 렌더 사이클 밖에서 Hackle variation을 조회한다.
 * useCustomVariation(hook)과 달리 호출 시점을 제어할 수 있어, "노출(모수 등록) 시점"을 원하는
 * 사용자 행동 뒤로 미룰 수 있다. 동기 함수라 깜빡임(A→B flip)이 없다.
 *
 * ⚠️ goi_common에도 동명 함수가 있으나 그쪽은 자체 createInstance로 만든 **별개 인스턴스**를 쓴다.
 * HackleProvider와 HackleClient.setUser(_app.tsx)가 사용하는 인스턴스는 이 파일의 것이므로,
 * 유저 컨텍스트가 일치하는 variation을 얻으려면 반드시 이 로컬 구현을 써야 한다.
 *
 * ⚠️ 이 함수는 GA4 experiment_exposure 이벤트를 발화하지 않는다(useCustomVariation이 하던 일).
 * 필요하면 호출부에서 직접 발화할 것.
 *
 * @param experimentKey 실험 키
 * @returns "A" | "B" | ... | "Z"(미할당/미해결)
 */
export const getVariationOnDemand = (experimentKey: number): string => {
  if (typeof window !== "undefined" && isDevEnvironment()) {
    registerExperiment(experimentKey);

    const stored = window.localStorage.getItem(HACKLE_DEVTOOLS_STORAGE_KEY);
    if (stored) {
      try {
        const variations: VariationMap = JSON.parse(stored);
        const testVariation = variations[experimentKey];
        if (HACKLE_VARIATIONS.includes(testVariation as never)) {
          return testVariation;
        }
      } catch {
        // ignore
      }
    }
  }

  return HackleClient.variation(experimentKey);
};
